/**
 * User-friendly error message formatting utilities
 */

export interface ErrorMessage {
  title: string
  message: string
}

/**
 * Format zone-related errors into user-friendly messages
 */
export const formatZoneError = (error: unknown): ErrorMessage => {
  let errorMessage = 'Unknown error occurred'

  if (error instanceof Error) {
    errorMessage = error.message
  } else if (typeof error === 'string') {
    errorMessage = error
  }

  // Map backend error messages to user-friendly messages
  const errorMappings: { [key: string]: ErrorMessage } = {
    // Zone validation errors
    'Invalid polygon format': {
      title: 'Invalid Zone Shape',
      message:
        'The zone shape is invalid. Please check the zone boundaries and try again.',
    },
    'Valid polygon with coordinates is required': {
      title: 'Zone Shape Required',
      message:
        'Please draw a valid zone shape on the map before creating or updating the zone.',
    },
    'Score must be between 0 and 6': {
      title: 'Invalid Risk Score',
      message:
        'The risk score must be a number between 0 and 6. Please select a valid score.',
    },
    'Zone name is required': {
      title: 'Zone Name Required',
      message: 'Please enter a name for this zone.',
    },
    'Zone name cannot be empty': {
      title: 'Zone Name Required',
      message: 'Please enter a name for this zone.',
    },

    // Permission errors
    'Access denied. Insufficient permissions.': {
      title: 'Access Denied',
      message:
        'You do not have permission to perform this action. Please contact an administrator if you believe this is an error.',
    },
    'Zone not found': {
      title: 'Zone Not Found',
      message:
        'The zone you are trying to access could not be found. It may have been deleted.',
    },
    'Authentication required': {
      title: 'Session Expired',
      message:
        'Your session has expired. Please refresh the page and log in again.',
    },
    'Authentication required.': {
      title: 'Session Expired',
      message:
        'Your session has expired. Please refresh the page and log in again.',
    },

    // Network errors
    'Failed to fetch': {
      title: 'Connection Error',
      message:
        'Unable to connect to the server. Please check your internet connection and try again.',
    },
    'Network request failed': {
      title: 'Connection Error',
      message:
        'Unable to connect to the server. Please check your internet connection and try again.',
    },
  }

  // Check for exact match first
  if (errorMessage in errorMappings) {
    return errorMappings[errorMessage]!
  }

  // Check for partial matches (for validation errors that might have additional text)
  for (const [key, value] of Object.entries(errorMappings)) {
    if (errorMessage.toLowerCase().includes(key.toLowerCase())) {
      return value
    }
  }

  // Check for common error patterns
  if (
    errorMessage.includes('network') ||
    errorMessage.includes('fetch') ||
    errorMessage.includes('connection')
  ) {
    return {
      title: 'Connection Error',
      message:
        'Unable to connect to the server. Please check your internet connection and try again.',
    }
  }

  if (errorMessage.includes('polygon') || errorMessage.includes('shape')) {
    return {
      title: 'Zone Shape Error',
      message:
        'There was an issue with the zone shape. Please check the zone boundaries and try again.',
    }
  }

  // Default user-friendly error
  return {
    title: 'Operation Failed',
    message:
      'We encountered an issue while processing your request. Please check your input and try again. If the problem persists, contact support.',
  }
}

/**
 * Format general API errors into user-friendly messages
 */
export const formatApiError = (error: unknown): ErrorMessage => {
  let errorMessage = 'Unknown error occurred'

  if (error instanceof Error) {
    errorMessage = error.message
  } else if (typeof error === 'string') {
    errorMessage = error
  }

  // Check if it's a zone error first
  const zoneError = formatZoneError(error)
  if (zoneError.title !== 'Operation Failed' || errorMessage.includes('zone')) {
    return zoneError
  }

  // General API error mappings
  const errorMappings: { [key: string]: ErrorMessage } = {
    'Failed to fetch': {
      title: 'Connection Error',
      message:
        'Unable to connect to the server. Please check your internet connection and try again.',
    },
    'Network request failed': {
      title: 'Connection Error',
      message:
        'Unable to connect to the server. Please check your internet connection and try again.',
    },
    Unauthorized: {
      title: 'Session Expired',
      message:
        'Your session has expired. Please refresh the page and log in again.',
    },
    Forbidden: {
      title: 'Access Denied',
      message:
        'You do not have permission to perform this action. Please contact an administrator.',
    },
    'Not Found': {
      title: 'Resource Not Found',
      message:
        'The requested resource could not be found. It may have been deleted.',
    },
  }

  // Check for exact match
  if (errorMessage in errorMappings) {
    return errorMappings[errorMessage]!
  }

  // Check for partial matches
  for (const [key, value] of Object.entries(errorMappings)) {
    if (errorMessage.toLowerCase().includes(key.toLowerCase())) {
      return value
    }
  }

  // Default
  return {
    title: 'Error',
    message:
      'An unexpected error occurred. Please try again. If the problem persists, contact support.',
  }
}

/**
 * Format delete operation errors
 */
export const formatDeleteError = (error: unknown): ErrorMessage => {
  const baseError = formatApiError(error)

  if (baseError.title === 'Operation Failed' || baseError.title === 'Error') {
    return {
      title: 'Delete Failed',
      message:
        'Unable to delete this item. Please try again. If the problem persists, contact support.',
    }
  }

  return baseError
}

/**
 * Format create operation errors
 */
export const formatCreateError = (error: unknown): ErrorMessage => {
  const baseError = formatZoneError(error)

  if (baseError.title === 'Operation Failed') {
    return {
      title: 'Creation Failed',
      message:
        'Unable to create this item. Please check your input and try again. If the problem persists, contact support.',
    }
  }

  return baseError
}
